import { useUILanguage } from 'hooks/useUILanguage';
import { useCallback, useEffect, useRef } from 'react';
import logger from 'services/logger';
import { EventStack } from './EventStack';
import { ImpressionEvent, ObservableLabels } from './types';
import { useCookies } from './useCookies';
import { useYleSk } from './useYleSk';

const SEND_INTERVAL = 5000;

type Props = {
  uiElementPosition: number;
  uiElementType: string;
};

export const useShowObserver = ({
  uiElementPosition,
  uiElementType,
}: Props) => {
  const showObserverRef = useRef<IntersectionObserver | null>(null);
  const eventStackRef = useRef(new EventStack());
  const labelsForElementMapRef = useRef(new Map<Element, ObservableLabels>());
  const cookieAttributesRef = useCookies();
  const yle_ui_language = useUILanguage();
  const yle_sk = useYleSk();

  const observe = useCallback(
    (element: HTMLDivElement, labels: ObservableLabels) => {
      if (!window.IntersectionObserver) {
        logger.error('No IntersectionObserver support');
        return;
      }
      if (element) {
        labelsForElementMapRef.current.set(element, labels);

        if (showObserverRef.current) {
          showObserverRef.current.observe(element);
        } else {
          logger.error('No IntersectionObserver instance');
        }
      }
    },
    []
  );

  useEffect(() => {
    if (!window.IntersectionObserver) {
      logger.error('No IntersectionObserver support');
      return;
    }

    const showObserver = (showObserverRef.current = new IntersectionObserver(
      (
        entries: IntersectionObserverEntry[],
        observer: IntersectionObserver
      ) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target;
            const observableLabels =
              labelsForElementMapRef.current.get(element);

            if (!observableLabels) {
              logger.error('No labels for element');
              return;
            }

            const eventData: ImpressionEvent = {
              impression_type: 'show',
              impression_timestamp: Date.now(),
              yle_sk,
              yle_vrs_platform: 'desktop',
              yle_vrs_device: 'browser',
              yle_ui_language,
              service_id: 'areena',
              ui_element_position: uiElementPosition,
              ui_element_type: uiElementType,
              ...observableLabels,
              ...cookieAttributesRef.current,
            };
            eventStackRef.current.addEvent(eventData);
            observer.unobserve(element);
            labelsForElementMapRef.current.delete(element);
          }
        });
      }
    ));

    const eventStack = eventStackRef.current;

    const intervalId = setInterval(() => {
      eventStack.sendEvents();
    }, SEND_INTERVAL);

    return () => {
      eventStack.sendEvents();
      clearInterval(intervalId);
      showObserver.disconnect();
    };
  }, [
    cookieAttributesRef,
    uiElementPosition,
    uiElementType,
    yle_sk,
    yle_ui_language,
  ]);

  return observe;
};
